import { isMicroInverterType, matchesPrimaryInverterType } from './inverter';
import {
  buildEffectiveInlineHardwareSpec,
  sanitizeInlineHardwareSpec
} from '../hardware/hardware-selection';

function finitePositive(value, fallback = 0) {
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? number : fallback;
}

function collectionLength(value) {
  if (Array.isArray(value)) return value.length;
  if (Array.isArray(value?.points)) return value.points.length;
  return 0;
}

export function countPlacedDesignerPanels(panels = []) {
  return (Array.isArray(panels) ? panels : []).reduce(
    (total, segment) =>
      total +
      collectionLength(segment?.horizontal) +
      collectionLength(segment?.vertical),
    0
  );
}

export function countSelectedDesignerPanels(panels = []) {
  return (Array.isArray(panels) ? panels : []).reduce((total, segment) => {
    const horizontal = Array.isArray(segment?.horizontal?.selected)
      ? segment.horizontal.selected.filter(Boolean).length
      : 0;
    const vertical = Array.isArray(segment?.vertical?.selected)
      ? segment.vertical.selected.filter(Boolean).length
      : 0;
    return total + horizontal + vertical;
  }, 0);
}

export function calculateOptimalInverterQuantity({
  inverter,
  panel,
  panels = [],
  targetDcAcRatio = 0.95,
  maximumQuantity = 10
} = {}) {
  if (!inverter || !Array.isArray(panels) || panels.length === 0) return 1;
  const panelCount = countPlacedDesignerPanels(panels);
  if (isMicroInverterType(inverter?.type)) return Math.max(1, panelCount);
  const panelPower = finitePositive(panel?.power);
  const inverterCapacity = finitePositive(inverter?.capacity);
  if (!panelCount || !panelPower || !inverterCapacity) return 1;
  const ratio = finitePositive(targetDcAcRatio, 0.95);
  return Math.max(
    1,
    Math.min(
      finitePositive(maximumQuantity, 10),
      Math.ceil((panelCount * panelPower) / (inverterCapacity * ratio))
    )
  );
}

export function applyAutomaticInverterQuantity(state) {
  const installationPackage = state?.package || {};
  const inverter = installationPackage.inverter;
  const additionalInverters = installationPackage.additional_inverters || [];
  const shouldUpdate =
    isMicroInverterType(inverter?.type) ||
    (state?.primaryInverterAutoQuantity && additionalInverters.length === 0);
  if (!shouldUpdate) return state;
  const inverterQuantity = calculateOptimalInverterQuantity({
    inverter,
    panel: installationPackage.panel,
    panels: state?.panels
  });
  return {
    ...state,
    package: {
      ...installationPackage,
      inverterQuantity
    }
  };
}

export function calculateInverterClipping({
  inverter,
  inverterQuantity = 1,
  additionalInverters = [],
  panel,
  panels = [],
  peakSunHours = 5.5,
  clippingFactor = 0.7,
  daysPerYear = 365
} = {}) {
  const empty = { clippedPower: 0, clippingPercentage: 0 };
  if (!inverter || !panel || !Array.isArray(panels) || !panels.length) {
    return empty;
  }
  const panelCount = countSelectedDesignerPanels(panels);
  const totalPanelPower = panelCount * finitePositive(panel?.power);
  if (!totalPanelPower) return empty;
  const additionalCapacity = (
    Array.isArray(additionalInverters) ? additionalInverters : []
  ).reduce(
    (total, item) =>
      total +
      finitePositive(item?.capacity) * finitePositive(item?.quantity, 1),
    0
  );
  const totalInverterCapacity =
    finitePositive(inverter?.capacity) * finitePositive(inverterQuantity, 1) +
    additionalCapacity;
  if (!totalInverterCapacity) return empty;
  const efficiency =
    finitePositive(
      inverter?.efficiency,
      isMicroInverterType(inverter?.type) ? 96 : 97
    ) / 100;
  const expectedACPower = totalPanelPower * efficiency;
  const clippedACPower = Math.max(0, expectedACPower - totalInverterCapacity);
  const clippedPower = clippedACPower / efficiency;
  return {
    clippedPower,
    clippingPercentage: (clippedPower / totalPanelPower) * 100,
    clippedAnnualKWh:
      (clippedPower / 1000) *
      finitePositive(peakSunHours, 5.5) *
      finitePositive(clippingFactor, 0.7) *
      finitePositive(daysPerYear, 365),
    expectedACPower,
    actualACPower: Math.min(expectedACPower, totalInverterCapacity),
    dcToACRatio: totalPanelPower / totalInverterCapacity
  };
}

export function selectDesignerPanelHardware(
  state,
  panel,
  { isRegisteredHardwareId } = {}
) {
  if (!panel || typeof panel !== 'object') {
    return { state, selectedPanel: null, persistence: null, pending: false };
  }
  const installationPackage = state?.package || {};
  const length = Number(panel.length);
  const width = Number(panel.width);
  const normalizedPanel = {
    ...panel,
    ...(Number.isFinite(length) ? { length: Math.max(500, length) } : {}),
    ...(Number.isFinite(width) ? { width: Math.max(500, width) } : {})
  };
  if (!Number.isFinite(length)) delete normalizedPanel.length;
  if (!Number.isFinite(width)) delete normalizedPanel.width;
  const hasPanelId =
    normalizedPanel.id !== undefined && normalizedPanel.id !== null;
  const registeredPanelId =
    hasPanelId &&
    (typeof isRegisteredHardwareId !== 'function' ||
      isRegisteredHardwareId(normalizedPanel.id, normalizedPanel))
      ? String(normalizedPanel.id)
      : null;
  const selectedPanel = registeredPanelId
    ? normalizedPanel
    : buildEffectiveInlineHardwareSpec(
        installationPackage.panel,
        normalizedPanel
      );
  if (!selectedPanel) {
    return { state, selectedPanel: null, persistence: null, pending: false };
  }
  const pending = countPlacedDesignerPanels(state?.panels) > 0;
  const selectedPanelSpec = registeredPanelId ? null : selectedPanel;
  return {
    state: {
      ...state,
      package: {
        ...installationPackage,
        ...(pending
          ? { pendingPanel: selectedPanel }
          : {
              panel: selectedPanel,
              pendingPanel: null,
              selectedPanelSpec
            })
      }
    },
    selectedPanel,
    pending,
    persistence: pending
      ? null
      : {
          selected_panel_id: registeredPanelId,
          selected_panel_spec: selectedPanelSpec
        }
  };
}

export function applyPendingDesignerPanelHardware(state) {
  const installationPackage = state?.package || {};
  const pendingPanel = installationPackage.pendingPanel;
  if (!pendingPanel) {
    return { state, selectedPanel: null, persistence: null };
  }
  const registeredPanelId =
    pendingPanel.id !== undefined && pendingPanel.id !== null
      ? String(pendingPanel.id)
      : null;
  const selectedPanelSpec = registeredPanelId
    ? null
    : sanitizeInlineHardwareSpec(pendingPanel);
  return {
    state: {
      ...state,
      package: {
        ...installationPackage,
        panel: pendingPanel,
        pendingPanel: null,
        selectedPanelSpec
      }
    },
    selectedPanel: pendingPanel,
    persistence: {
      selected_panel_id: registeredPanelId,
      selected_panel_spec: selectedPanelSpec
    }
  };
}

export function changeDesignerInverterQuantity(state, quantity) {
  return {
    state: {
      ...state,
      package: {
        ...(state?.package || {}),
        inverterQuantity: quantity,
        hasAutoCalculatedInverterQuantity: true
      },
      hasAutoCalculatedInverterQuantity: true,
      primaryInverterAutoQuantity: false
    },
    persistence: {
      inverter_quantity: quantity,
      primary_inverter_auto_quantity: false
    }
  };
}

export function changeDesignerAdditionalInverters(
  state,
  additionalInverters = []
) {
  const installationPackage = state?.package || {};
  const primary = installationPackage.inverter;
  const catalog = state?.inverters || [];
  const filtered = isMicroInverterType(primary?.type)
    ? []
    : (Array.isArray(additionalInverters) ? additionalInverters : []).filter(
        (item) => {
          if (!item?.id && !item?.inverter_id) return true;
          const id = item.id ?? item.inverter_id;
          const candidate = catalog.find(
            (inverter) => String(inverter?.id) === String(id)
          );
          return matchesPrimaryInverterType(primary, candidate || item);
        }
      );
  const hasConfiguredAdditionalInverters = filtered.some(
    (item) => item?.id || item?.inverter_id
  );
  const currentAuto =
    state?.primaryInverterAutoQuantity ??
    installationPackage.primaryInverterAutoQuantity ??
    true;
  const primaryInverterAutoQuantity = hasConfiguredAdditionalInverters
    ? false
    : currentAuto;
  return {
    state: {
      ...state,
      package: {
        ...installationPackage,
        additional_inverters: filtered,
        primaryInverterAutoQuantity,
        hasAutoCalculatedInverterQuantity: !primaryInverterAutoQuantity
      },
      primaryInverterAutoQuantity,
      hasAutoCalculatedInverterQuantity: !primaryInverterAutoQuantity
    },
    persistence: {
      additional_inverters: filtered,
      primary_inverter_auto_quantity: primaryInverterAutoQuantity
    }
  };
}
